import React, { useContext, useMemo, useState } from 'react';

import { Pencil, Sparkles, Trash } from 'lucide-react';
import { compose } from 'redux';

import AJAX from 'common/AJAX';
import connect from 'common/core/connect';
import DataTable, { type Column } from 'common/DataTable';
import Helmet from 'common/helmets/Helmet';
import useMutation from 'common/hooks/useMutation';
import ModernConfirmModal from 'common/modals/ModernConfirmModal';
import withAccessControl from 'common/routing/withAccessControl';
import EmptyState from 'common/subdomain/admin/reports/common/EmptyState';
import { IdeasConfigurationContext } from 'common/subdomain/admin/v2/containers/IdeasConfigurationContainer';
import AdminV2IdeaThemeModal from 'common/subdomain/admin/v2/modals/AdminV2IdeaThemeModal';
import ButtonV2 from 'common/ui/ButtonV2';
import HStack from 'common/ui/HStack';
import IconBadge from 'common/ui/IconBadge';
import IconButtonV2 from 'common/ui/IconButtonV2';
import SwitchV2 from 'common/ui/SwitchV2';
import { H2, P } from 'common/ui/Text';
import TextInputV2 from 'common/ui/TextInputV2';
import VStack from 'common/ui/VStack';
import findStringMatches from 'common/util/findStringMatches';
import nbspLastSpace from 'common/util/nbspLastSpace';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';
import useDelayer from 'common/util/useDelayer';

import type { IdeaTheme, IdeasConfiguration } from 'common/api/endpoints/idea';
import type { IdeasConfigurationState } from 'common/reducers/ideasConfiguration';

type ConnectProps = {
  ideaThemes: IdeasConfiguration['themes'];
  loading: boolean;
};
type OwnProps = Record<string, never>;

type Props = ConnectProps;

type ThemeUpdateData = Partial<{ autopilotEnabled: boolean; description: string; name: string }> & {
  themeID: string;
};

type Row = {
  id: string;
  autopilotEnabled: boolean;
  createdByAutopilot: boolean;
  description: string;
  name: string;
};

enum Modals {
  create = 'create',
  delete = 'delete',
  update = 'update',
}

const AdminIdeaThemesSettings = ({ ideaThemes, loading }: Props) => {
  const { addThemeToConfiguration, reloadConfiguration } = useContext(IdeasConfigurationContext);
  const [modal, setModal] = useState<Modals | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<IdeaTheme | null>(null);
  const [search, setSearch] = useState('');

  const columns = [
    {
      id: 'name',
      align: 'left',
      header: 'Name',
      cell: (name, row) => (
        <HStack className="gap-2 w-[150px] items-center">
          <P>{name}</P>
          {row.createdByAutopilot ? <IconBadge active icon={Sparkles} size="small" /> : null}
        </HStack>
      ),
      sortable: false,
    } as Column<Row, 'name'>,
    {
      id: 'description',
      align: 'left',
      header: 'Description',
      cell: (description) => <P>{nbspLastSpace(description)}</P>,
      sortable: false,
      fullWidth: true,
    } as Column<Row, 'description'>,
    {
      id: 'autopilotEnabled',
      align: 'left',
      header: 'Assign automatically?',
      cell: (autopilotEnabled, row) => {
        const theme = ideaThemes.find((theme) => theme._id === row.id);
        if (!theme) {
          return null;
        }

        return (
          <div className="w-[150px]">
            <SwitchV2
              checked={autopilotEnabled}
              onChange={() => {
                // optimistically update the theme
                addThemeToConfiguration({ ...theme, autopilotEnabled: !autopilotEnabled });

                delayedUpdateTheme({ themeID: theme._id, autopilotEnabled: !autopilotEnabled });
              }}
            />
          </div>
        );
      },
      sortable: false,
    } as Column<Row, 'autopilotEnabled'>,
    {
      id: 'id',
      align: 'right',
      header: '',
      cell: (id) => {
        const theme = ideaThemes.find((theme) => theme._id === id);
        if (!theme) {
          return null;
        }

        return (
          <HStack className="gap-2">
            <IconButtonV2
              aria-label="Edit theme"
              disabled={updateTheme.isLoading}
              size="small"
              variant="plain"
              icon={Pencil}
              loading={updateTheme.isLoading}
              onClick={() => {
                setSelectedTheme(theme);
                setModal(Modals.update);
              }}></IconButtonV2>
            <IconButtonV2
              aria-label="Delete theme"
              disabled={deleteTheme.isLoading}
              size="small"
              variant="plain"
              icon={Trash}
              loading={deleteTheme.isLoading}
              onClick={() => {
                setSelectedTheme(theme);
                setModal(Modals.delete);
              }}></IconButtonV2>
          </HStack>
        );
      },
      sortable: false,
    } as Column<Row, 'id'>,
  ];

  const createTheme = useMutation<{ theme: IdeaTheme }, { name: string; description: string }>({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/ideas/themes/create', data);
      return response;
    },
    isResponseSuccessful: (response) => !!response.theme,
    onSuccess: async (data) => {
      await addThemeToConfiguration(data.theme);
      setModal(null);
    },
  });

  const updateTheme = useMutation<string, ThemeUpdateData>({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/ideas/themes/update', data);
      return response;
    },
    isResponseSuccessful: (response) => response === 'success',
    onSuccess: async (_, variables) => {
      const existingTheme = ideaThemes.find((theme) => theme._id === variables.themeID);
      if (!existingTheme) {
        return;
      }

      await addThemeToConfiguration({ ...existingTheme, ...variables });
      setModal(null);
    },
  });

  const deleteTheme = useMutation<string, { themeID: string }>({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/ideas/themes/delete', data);
      return response;
    },
    isResponseSuccessful: (response) => response === 'success',
    onSuccess: async () => {
      await reloadConfiguration();
      setModal(null);
    },
  });

  const delayedUpdateTheme = useDelayer(
    (updates: ThemeUpdateData) => updateTheme.mutate(updates),
    500,
  );

  const rows = useMemo(() => {
    const filteredIdeaThemes = ideaThemes ? findStringMatches(ideaThemes, 'name', search) : [];
    return filteredIdeaThemes.map((theme) => ({
      id: theme._id,
      autopilotEnabled: theme.autopilotEnabled,
      createdByAutopilot: theme.createdByAutopilot,
      description: theme.description,
      name: theme.name,
    }));
  }, [ideaThemes, search]);

  return (
    <>
      <Helmet title="Idea Themes | Canny" />
      <VStack className="gap-2">
        <H2 variant="headingMd">Themes</H2>
        <P className="text-gray-110">
          Themes help you organize and identify recurring topics across your ideas. Assign themes
          automatically to detect patterns and highlight trends in your&nbsp;ideas.
        </P>
        <div className="flex w-full justify-end gap-2">
          {ideaThemes?.length > 5 ? (
            <TextInputV2
              placeholder="Search themes"
              onChange={(value) => setSearch(value)}
              value={search}
            />
          ) : null}
          <ButtonV2 loading={createTheme.isLoading} onClick={() => setModal(Modals.create)}>
            Create new theme
          </ButtonV2>
        </div>
        <DataTable
          columns={columns}
          emptyState={
            <tr>
              <td colSpan={columns.length}>
                <EmptyState title={null} icon={null} message="No themes were created yet" />
              </td>
            </tr>
          }
          hoverable={false}
          rows={rows}
          loading={loading}
          showLoadingWithHeaders={true}
        />
      </VStack>
      {modal === Modals.create && (
        <AdminV2IdeaThemeModal
          loading={createTheme.isLoading}
          onClose={() => setModal(null)}
          mode={Modals.create}
          onSubmit={(theme: IdeaTheme) => {
            createTheme.mutate({ name: theme.name, description: theme.description });
          }}
        />
      )}
      {modal === Modals.update && (
        <AdminV2IdeaThemeModal
          loading={updateTheme.isLoading}
          onClose={() => setModal(null)}
          mode={Modals.update}
          initialData={selectedTheme!}
          onSubmit={(theme: IdeaTheme) => {
            updateTheme.mutate({
              name: theme.name,
              description: theme.description,
              themeID: theme._id,
            });
          }}
        />
      )}
      {modal === Modals.delete && (
        <ModernConfirmModal
          onClose={() => setModal(null)}
          onConfirm={() => {
            deleteTheme.mutate({ themeID: selectedTheme?._id ?? '' });
          }}
          confirmText="Delete"
          type="destructive"
          header={`Delete the "${selectedTheme?.name}" theme`}>
          <P>
            Are you sure you want to delete this theme? This action cannot be undone. This theme
            will no longer be available for auto-theming. The theme will be removed from
            all&nbsp;ideas.
          </P>
        </ModernConfirmModal>
      )}
    </>
  );
};

export default compose(
  connect(({ ideasConfiguration }: { ideasConfiguration: IdeasConfigurationState }) => ({
    ideaThemes: ideasConfiguration.data?.themes ?? [],
    loading: ideasConfiguration.loading,
  })),
  withAccessControl(
    testEveryPermission(RoutePermissions.adminSettings.postFields.themes),
    '/admin/settings',
    { forwardRef: true },
  ),
)(AdminIdeaThemesSettings) as unknown as React.FC<OwnProps>;
